<template>
  <CheckoutStep>
    <div class="space-y-24">
      <h1 class="heading-3 mb-12 mt-24 md:mb-24 md:mt-0">
        {{ i18n(translations.mainTitle) }}
      </h1>

      <RevCard>
        <p class="text-static-danger-hi body-1-bold p-16 pb-0 md:p-24 md:pb-0">
          {{ i18n(translations.outOfStock) }}
        </p>

        <div v-if="status === 'pending'" class="p-16 md:p-24">
          <div class="flex w-full gap-12 lg:gap-20">
            <RevSkeleton
              class="size-60 md:size-[110px]"
              :loading="true"
              shape="rectangle"
            />

            <div class="h-32 grow">
              <RevSkeleton class="w-full" :loading="true" shape="rectangle" />
            </div>
          </div>
        </div>

        <div
          v-if="original && replacements"
          class="flex justify-between gap-12 p-16 md:p-24"
        >
          <div class="flex gap-12 lg:gap-20">
            <ProductIllustration
              class="size-60 opacity-50 grayscale-[50%] md:size-[110px]"
              :image="original.image"
              :title="original.title"
            />

            <Description
              :category-id="2"
              :grade-id="original.grade.value"
              :grade-name="original.grade.name"
              :has-new-battery="false"
              is-out-of-stock
              :title="original.title"
            />
          </div>

          <div class="text-static-default-hi-disabled heading-3">
            {{ i18n.price(original.price) }}
          </div>
        </div>

        <Replacement
          v-if="original && replacements.length"
          :category-id="2"
          :original
          :replacements
          @replaced="emit('replaced', $event)"
        />

        <div v-else class="p-16 md:p-24">
          <NoReplacementWarning v-if="status !== 'pending'" />
        </div>
      </RevCard>

      <CancelOrder />
    </div>
  </CheckoutStep>
</template>

<script setup lang="ts">
import { useAsyncData } from '#app'
import { computed } from 'vue'

import type { MonetaryAmount } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevSkeleton } from '@ds/components/Skeleton'

import Description from '~/scopes/checkout/components/Cart/components/CartItemCard/components/Product/components/Description/Description.vue'
import ProductIllustration from '~/scopes/checkout/components/Cart/components/CartItemCard/components/Product/components/ProductIllustration/ProductIllustration.vue'
import Replacement from '~/scopes/checkout/components/Cart/components/CartItemCard/components/Replacement/Replacement.vue'
import CheckoutStep from '~/scopes/checkout/components/CheckoutStep/CheckoutStep.vue'
import CancelOrder from '~/scopes/checkout/flows/bouygues-in-store/components/CancelOrder/CancelOrder.vue'

import NoReplacementWarning from '../NoReplacementWarning/NoReplacementWarning.vue'

import translations from './OutOfStock.translations'

const props = defineProps<{
  listingPublicId: string
  listingGrade: number
  listingPrice: MonetaryAmount
  offerId: string
}>()

const $httpFetch = useHttpFetch()
const i18n = useI18n()

const emit = defineEmits<{
  (e: 'replaced', listingPublicId: string): void
}>()

const { data, status } = useAsyncData(() =>
  $httpFetch('/api/checkout/cart/listings/v1/replacement', {
    query: {
      listing_ids: `${props.listingPublicId}`,
      mobile_plan_offer_id: props.offerId,
      grade_id: props.listingGrade,
      price: props.listingPrice.amount,
      currency: props.listingPrice.currency,
    },
  }),
)

const item = computed(() => {
  if (!data.value) {
    return null
  }

  return (
    data.value.find(
      ({ listingPublicId }) => listingPublicId === props.listingPublicId,
    ) ?? null
  )
})

const original = computed(() => {
  return item.value?.original ?? null
})

const replacements = computed(() => {
  return item.value?.replacements ?? []
})
</script>
